import { useEffect, useRef } from 'react'
import { Route, Routes, useNavigate } from 'react-router-dom'
import { Slide, ToastContainer } from 'react-toastify'
import ErrorBoundary from '../../../components/molecules/errorBoundary/ErrorBoundary'
import { PrivateLayout } from '../../../components/molecules/privateRoute/PrivateRoute'
import routes from '../../../config/routes'
import { useIsLogged } from '../../../modules/auth/hooks'
import { Notifications as NotificationsService } from '../../../modules/notifications/service'
import LoginScreen from '../auth/login/LoginScreen'
import RecoverPasswordCompleteScreen from '../auth/password/complete/RecoverPasswordCompleteScreen'
import ForgotPasswordEmailSentScreen from '../auth/password/emailSent/ForgotPasswordEmailSentScreen'
import ForgotPasswordScreen from '../auth/password/forgot/ForgotPasswordScreen'
import RecoverPasswordScreen from '../auth/password/recover/RecoverPasswordScreen'
import RegisterScreen from '../auth/register/RegisterScreen'
import CoworkingValidationPendingScreen from '../auth/validation/coworkingValidationPending/CoworkingValidationPendingScreen'
import EmailValidationPendingScreen from '../auth/validation/emailValidationPending/EmailValidationPendingScreen'
import ValidateScreen from '../auth/validation/validate/ValidateScreen'
import WelcomeScreen from '../auth/welcome/WelcomeScreen'
import WelcomeRegisterScreen from '../auth/welcomeRegister/WelcomeRegisterScreen'
import CookiesScreen from '../cookies/CookiesScreen'
import GDPRScreen from '../gdpr/GDPRScreen'
import GUCSLScreen from '../gucsl/GUCSLScreen'
import HomeScreen from '../home/HomeScreen'
import OnBoardingCompleteScreen from '../onboarding/complete/OnBoardingCompleteScreen'
import OnBoardingCreationScreen from '../onboarding/creation/OnBoardingCreationScreen'
import OnBoardingHomeScreen from '../onboarding/home/OnBoardingHomeScreen'
import OnBoardingProfileScreen from '../onboarding/profile/OnBoardingProfileScreen'
import PaymentKOScreen from '../payments/ko/KO'
import PaymentOkScreen from '../payments/ok/Ok'
import PaymentRenewKOScreen from '../payments/renew/ko/KO'
import PaymentRenewOkScreen from '../payments/renew/ok/Ok'
import GeolocationScreen from '../permissions/geolocation/GeolocationScreen'
import NotificationsScreen from '../permissions/notifications/NotificationsScreen'
import EditProfileScreen from '../profile/edit/EditProfileScreen'
import ProfileScreen from '../profile/home/ProfileScreen'
import ReservationCancelScreen from '../reservations/cancel/ReservationCancelScreen'
import CancelReservationScreen from '../reservations/cancelReservation/CancelReservationScreen'
import BookChangeDateScreen from '../reservations/changeDate/BookChangeDateScreen'
import ReservationDetailsScreen from '../reservations/details/ReservationDetailsScreen'
import ReservationsListScreen from '../reservations/list/ReservationsListScreen'
import MeetingRoomAvailabilityScreen from '../reservations/meetingRoom/availability/MeetingRoomAvailability'
import MeetingRoomChangeSlotScreen from '../reservations/meetingRoom/change/MeetingRoomChangeSlotScreen'
import BookMeetingRoomScreen from '../reservations/meetingRoom/details/BookMeetingRoomScreen'
import BookMeetingRoomSuccessScreen from '../reservations/meetingRoom/success/BookMeetingRoomSuccessScreen'
import MeetingRoomDetailsScreen from '../reservations/meetingRoomDetails/MeetingRoomDetailsScreen'
import ReservationPaymentScreen from '../reservations/payment/ReservationPaymentScreen'
import BookPlaceScreen from '../reservations/place/details/BookPlaceScreen'
import BookChangeSeatScreen from '../reservations/seat/change/BookChangeSeatScreen'
import BookSeatConfirmationScreen from '../reservations/seat/confirmation/BookSeatConfirmationScreen'
import BookSeatScreen from '../reservations/seat/details/BookSeatScreen'
import BookSeatPlaceScreen from '../reservations/seat/place/BookSeatPlaceScreen'
import BookSeatSuccessScreen from '../reservations/seat/success/BookSeatSuccessScreen'
import ReservationVouchersScreen from '../reservations/vouchers/ReservationVouchersScreen'
import WorkmateProfileScreen from '../reservations/workmateProfile/WorkmateProfileScreen'
import ReservationWorkmatesScreen from '../reservations/workmates/ReservationWorkmatesScreen'
import BillingAddressScreen from '../settings/account/billingAddress/BillingAddressScreen'
import CancelGUCSLScreen from '../settings/account/cancelGUCSL/CancelGUCSLScreen'
import GUCSLBenefitsScreen from '../settings/account/gucsl/GUCSLBenefitsScreen'
import InvoicesByYearScreen from '../settings/account/invoices/byYear/InvoicesByYearScreen'
import InvoicesHomeScreen from '../settings/account/invoices/home/InvoicesHomeScreen'
import PrivacyScreen from '../settings/account/privacy/PrivacyScreen'
import SavedCardsScreen from '../settings/account/savedCards/SavedCardsScreen'
import VouchersScreen from '../settings/account/vouchers/home/VouchersScreen'
import VoucherInvoicesScreen from '../settings/account/vouchers/invoices/VoucherInvoicesScreen'
import ReneweVoucherScreen from '../settings/account/vouchers/renew/RenewVoucherScreen'
import VoucherScreen from '../settings/account/vouchers/voucher/VoucherScreen'
import UpdateAttachmentsScreen from '../settings/attachments/update/UpdateAttachmentsScreen'
import UpdateDNIScreen from '../settings/attachments/updateDNI/UpdateDNIScreen'
import SettingsScreen from '../settings/home/SettingsScreen'
import SplashScreen from '../splash/SplashScreen'
import styles from './AppRootScreen.module.css'

// Helper to strip /app prefix from routes for nested routing
const stripAppPrefix = (route: string) => route.replace('/app', '')

export const AppRootScreen = () => {
  const navigate = useNavigate()
  const { isLogged } = useIsLogged()
  const pushRegisterd = useRef(false)

  useEffect(() => {
    if (pushRegisterd.current || !isLogged) {
      return
    }

    pushRegisterd.current = true
    NotificationsService.registerPush()
  }, [isLogged])

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div id="app-wrapper" className={styles.appWrapper}>
          <ErrorBoundary navigate={navigate}>
            <Routes>
              {/* Public routes */}
              <Route path={stripAppPrefix(routes.gdpr)} element={<GDPRScreen />} />
              <Route path={stripAppPrefix(routes.cookies)} element={<CookiesScreen />} />
              <Route path="" element={<SplashScreen />} />
              <Route path={stripAppPrefix(routes.welcome)} element={<WelcomeScreen />} />
              <Route path={stripAppPrefix(routes.registerWelcome)} element={<WelcomeRegisterScreen />} />
              <Route path={stripAppPrefix(routes.register)} element={<RegisterScreen />} />
              <Route path={stripAppPrefix(routes.login)} element={<LoginScreen />} />
              <Route path={stripAppPrefix(routes.password.forgot)} element={<ForgotPasswordScreen />} />
              <Route path={stripAppPrefix(routes.password.emailSent)} element={<ForgotPasswordEmailSentScreen />} />
              <Route path={stripAppPrefix(routes.password.recover)} element={<RecoverPasswordScreen />} />
              <Route path={stripAppPrefix(routes.password.complete)} element={<RecoverPasswordCompleteScreen />} />
              <Route path={stripAppPrefix(routes.validation.validate)} element={<ValidateScreen />} />
              <Route path={stripAppPrefix(routes.geolocation)} element={<GeolocationScreen />} />
              <Route path={stripAppPrefix(routes.notifications)} element={<NotificationsScreen />} />

              {/* Private routes */}
              <Route element={<PrivateLayout />}>
                <Route
                  path={stripAppPrefix(routes.validation.emailPending)}
                  element={<EmailValidationPendingScreen />}
                />
                <Route
                  path={stripAppPrefix(routes.validation.coworkingPending)}
                  element={<CoworkingValidationPendingScreen />}
                />

                <Route path={stripAppPrefix(routes.home)} element={<HomeScreen />} />
                <Route path={stripAppPrefix(routes.gucsl)} element={<GUCSLScreen />} />

                <Route path={stripAppPrefix(routes.payments.ok)} element={<PaymentOkScreen />} />
                <Route path={stripAppPrefix(routes.payments.ko)} element={<PaymentKOScreen />} />
                <Route path={stripAppPrefix(routes.payments.renew.ok)} element={<PaymentRenewOkScreen />} />
                <Route path={stripAppPrefix(routes.payments.renew.ko)} element={<PaymentRenewKOScreen />} />

                <Route path={stripAppPrefix(routes.settings.home)} element={<SettingsScreen />} />
                <Route path={stripAppPrefix(routes.settings.account.privacy)} element={<PrivacyScreen />} />
                <Route
                  path={stripAppPrefix(routes.settings.account.billingAddress)}
                  element={<BillingAddressScreen />}
                />
                <Route path={stripAppPrefix(routes.settings.account.savedCards)} element={<SavedCardsScreen />} />
                <Route path={stripAppPrefix(routes.settings.account.vouchers.home)} element={<VouchersScreen />} />
                <Route path={stripAppPrefix(routes.settings.account.invoices.home)} element={<InvoicesHomeScreen />} />
                <Route
                  path={stripAppPrefix(routes.settings.account.invoices.byYear)}
                  element={<InvoicesByYearScreen />}
                />
                <Route path={stripAppPrefix(routes.settings.account.vouchers.byId)} element={<VoucherScreen />} />
                <Route
                  path={stripAppPrefix(routes.settings.account.vouchers.invoices)}
                  element={<VoucherInvoicesScreen />}
                />
                <Route path={stripAppPrefix(routes.settings.account.gucsl.member)} element={<GUCSLBenefitsScreen />} />
                <Route path={stripAppPrefix(routes.settings.account.gucsl.cancel)} element={<CancelGUCSLScreen />} />
                <Route
                  path={stripAppPrefix(routes.settings.account.attachments.update)}
                  element={<UpdateAttachmentsScreen />}
                />
                <Route path={stripAppPrefix(routes.settings.account.attachments.dni)} element={<UpdateDNIScreen />} />

                <Route path={stripAppPrefix(routes.book.new.seat.renewWarning)} element={<ReneweVoucherScreen />} />
                <Route path={stripAppPrefix(routes.book.new.seat.details)} element={<BookSeatScreen />} />
                <Route path={stripAppPrefix(routes.book.new.seat.place)} element={<BookSeatPlaceScreen />} />
                <Route
                  path={stripAppPrefix(routes.book.new.seat.confirmation)}
                  element={<BookSeatConfirmationScreen />}
                />
                <Route path={stripAppPrefix(routes.book.new.seat.success)} element={<BookSeatSuccessScreen />} />
                <Route path={stripAppPrefix(routes.book.new.place.details)} element={<BookPlaceScreen />} />
                <Route
                  path={stripAppPrefix(routes.book.new.meetingsRoom.details)}
                  element={<BookMeetingRoomScreen />}
                />
                <Route
                  path={stripAppPrefix(routes.book.new.meetingsRoom.success)}
                  element={<BookMeetingRoomSuccessScreen />}
                />
                <Route
                  path={stripAppPrefix(routes.book.meetingRoom.availability)}
                  element={<MeetingRoomAvailabilityScreen />}
                />
                <Route path={stripAppPrefix(routes.book.list)} element={<ReservationsListScreen />} />
                <Route path={stripAppPrefix(routes.book.details)} element={<ReservationDetailsScreen />} />
                <Route path={stripAppPrefix(routes.book.meetingRoomDetails)} element={<MeetingRoomDetailsScreen />} />
                <Route path={stripAppPrefix(routes.book.changeMeetingRoom)} element={<MeetingRoomChangeSlotScreen />} />
                <Route path={stripAppPrefix(routes.book.changeSeat)} element={<BookChangeSeatScreen />} />
                <Route path={stripAppPrefix(routes.book.changeDate)} element={<BookChangeDateScreen />} />
                <Route path={stripAppPrefix(routes.book.workmates)} element={<ReservationWorkmatesScreen />} />
                <Route path={stripAppPrefix(routes.book.cancel)} element={<ReservationCancelScreen />} />
                <Route path={stripAppPrefix(routes.book.cancelReservation)} element={<CancelReservationScreen />} />
                <Route path={stripAppPrefix(routes.book.vouchers)} element={<ReservationVouchersScreen />} />
                <Route path={stripAppPrefix(routes.book.payment)} element={<ReservationPaymentScreen />} />

                <Route path={stripAppPrefix(routes.profile.details)} element={<ProfileScreen />} />
                <Route path={stripAppPrefix(routes.profile.edit)} element={<EditProfileScreen />} />

                <Route path={stripAppPrefix(routes.workmates.publicProfile)} element={<WorkmateProfileScreen />} />

                <Route path={stripAppPrefix(routes.onboarding.home)} element={<OnBoardingHomeScreen />} />
                <Route path={stripAppPrefix(routes.onboarding.profile)} element={<OnBoardingProfileScreen />} />
                <Route path={stripAppPrefix(routes.onboarding.creation)} element={<OnBoardingCreationScreen />} />
                <Route path={stripAppPrefix(routes.onboarding.complete)} element={<OnBoardingCompleteScreen />} />
              </Route>
            </Routes>

            <ToastContainer
              className={styles.toasts}
              position="top-center"
              newestOnTop
              draggable
              limit={1}
              transition={Slide}
            />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  )
}
